import { useEffect, useRef, useState } from "react";
import {
  getGetCustomerInvoiceQueryKey,
  getListCustomerInvoiceRemindersQueryKey,
  getListCustomerInvoicesQueryKey,
  useGetCustomerInvoice,
  useListCustomerInvoiceReminders,
  useListCustomerInvoices,
  useRecordSalePayment,
  useSendCustomerInvoiceReminder,
  useUpdateCustomerInvoice,
} from "@workspace/api-client-react";
import type { CustomerInvoice, CustomerInvoiceDetail } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import {
  AlertCircle,
  Bell,
  CalendarDays,
  Check,
  CheckCircle2,
  ChevronRight,
  Clock3,
  CreditCard,
  Download,
  Mail,
  MessageSquareText,
  ReceiptText,
  Search,
  ShieldCheck,
  X,
} from "lucide-react";
import { formatCurrency } from "@/lib/currency";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";

type InvoiceFilter = "open" | "overdue" | "settled" | "all";
type ReminderChannel = "sms" | "email";

const filters: { value: InvoiceFilter; label: string }[] = [
  { value: "open", label: "Open balances" },
  { value: "overdue", label: "Overdue" },
  { value: "settled", label: "Settled" },
  { value: "all", label: "All receipts" },
];
const ATTEMPT_REFERENCE_PREFIX = "retailpulse:customer-invoice-attempt:";
const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");

function currency(value: number) {
  return formatCurrency(value);
}

function dateLabel(value: string | null | undefined) {
  if (!value) return "Not set";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Not set";
  return date.toLocaleDateString("en-NG", { day: "numeric", month: "short", year: "numeric" });
}

function dateInputValue(value: string | null | undefined) {
  if (!value) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

function makeReference() {
  return typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `rp-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;
}

function agingText(invoice: CustomerInvoice) {
  if (invoice.status === "settled") return "Settled";
  if (invoice.agingBucket === "no_due_date") return "Due date not set";
  if (invoice.agingBucket === "not_due") return `${Math.max(0, invoice.daysUntilDue ?? 0)} days to due`;
  if (invoice.agingBucket === "due_today") return "Due today";
  if (invoice.daysPastDue === 1) return "1 day overdue";
  return `${invoice.daysPastDue} days overdue`;
}

function agingTone(invoice: CustomerInvoice) {
  if (invoice.status === "settled") return "bg-[#e6f1eb] text-[#2a694f]";
  if (invoice.agingBucket === "no_due_date") return "bg-[#ecefe9] text-[#67776e]";
  if (invoice.agingBucket === "not_due") return "bg-[#eaf1f0] text-[#4a6662]";
  if (invoice.agingBucket === "due_today") return "bg-[#fff1d9] text-[#8b5a15]";
  return "bg-[#f8e8e2] text-[#a34d38]";
}

function ReminderStatus({ status }: { status: string }) {
  const config: Record<string, { label: string; className: string }> = {
    sending: { label: "Handoff pending", className: "bg-[#fff1d9] text-[#815315]" },
    accepted: { label: "Accepted by provider", className: "bg-[#e6f1eb] text-[#2a694f]" },
    failed: { label: "Handoff failed", className: "bg-[#f8e8e2] text-[#a34d38]" },
    unknown: { label: "Handoff uncertain", className: "bg-[#f6eddc] text-[#815315]" },
  };
  const view = config[status] ?? config.unknown;
  return <span data-testid={`status-reminder-${status}`} className={`inline-flex items-center rounded-full px-2.5 py-1 text-[11px] font-semibold ${view.className}`}>{view.label}</span>;
}

function InvoiceStatus({ invoice }: { invoice: CustomerInvoice }) {
  const label = invoice.status === "partial" ? "Part paid" : invoice.status === "settled" ? "Settled" : "Unpaid";
  const color = invoice.status === "settled" ? "bg-[#e6f1eb] text-[#2a694f]" : invoice.status === "partial" ? "bg-[#e9eef2] text-[#49616c]" : "bg-[#f8e8e2] text-[#a34d38]";
  return <span data-testid={`status-invoice-${invoice.id}`} className={`rounded-full px-2.5 py-1 text-[11px] font-semibold ${color}`}>{label}</span>;
}

export default function Unpaid() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [filter, setFilter] = useState<InvoiceFilter>("open");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [cursorStack, setCursorStack] = useState<(string | null)[]>([null]);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [paymentInvoice, setPaymentInvoice] = useState<CustomerInvoice | null>(null);
  const [paymentAmount, setPaymentAmount] = useState("");
  const [reminderInvoice, setReminderInvoice] = useState<CustomerInvoice | null>(null);
  const [reminderChannel, setReminderChannel] = useState<ReminderChannel>("sms");
  const [acknowledgeUnknown, setAcknowledgeUnknown] = useState(false);
  const [dueDateDraft, setDueDateDraft] = useState("");
  const attemptReferences = useRef(new Map<string, string>());

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setCursorStack([null]);
      setDebouncedSearch(search.trim());
    }, 250);
    return () => window.clearTimeout(timer);
  }, [search]);

  const cursor = cursorStack[cursorStack.length - 1] ?? null;
  const invoiceQuery = useListCustomerInvoices({
    status: filter,
    search: debouncedSearch || undefined,
    limit: 50,
    cursor: cursor ?? undefined,
  });
  const list = invoiceQuery.data;
  const invoices = list?.invoices ?? [];
  const detailQuery = useGetCustomerInvoice(selectedId ?? 0, {
    query: {
      enabled: selectedId !== null,
      queryKey: getGetCustomerInvoiceQueryKey(selectedId ?? 0),
    },
  });
  const remindersQuery = useListCustomerInvoiceReminders(selectedId ?? 0, {
    query: {
      enabled: selectedId !== null,
      queryKey: getListCustomerInvoiceRemindersQueryKey(selectedId ?? 0),
    },
  });
  const updateInvoice = useUpdateCustomerInvoice();
  const recordPayment = useRecordSalePayment();
  const sendReminder = useSendCustomerInvoiceReminder();

  const visibleInvoices = invoices;

  const detail = detailQuery.data as CustomerInvoiceDetail | undefined;
  const reminderHistory = remindersQuery.data ?? detail?.reminders ?? [];
  const latestReminder = reminderInvoice?.latestReminder;
  const latestIsUnknown = latestReminder?.status === "unknown";

  async function invalidateInvoice(id: number, includeReminders = false) {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: getListCustomerInvoicesQueryKey() }),
      queryClient.invalidateQueries({ queryKey: getListCustomerInvoicesQueryKey({ status: "open" }) }),
      queryClient.invalidateQueries({ queryKey: getListCustomerInvoicesQueryKey({ status: "overdue" }) }),
      queryClient.invalidateQueries({ queryKey: getListCustomerInvoicesQueryKey({ status: "settled" }) }),
      queryClient.invalidateQueries({ queryKey: getListCustomerInvoicesQueryKey({ status: "all" }) }),
      queryClient.invalidateQueries({ queryKey: getGetCustomerInvoiceQueryKey(id) }),
      ...(includeReminders ? [queryClient.invalidateQueries({ queryKey: getListCustomerInvoiceRemindersQueryKey(id) })] : []),
    ]);
  }

  async function downloadInvoiceReport() {
    setIsExporting(true);
    try {
      const params = new URLSearchParams({ status: filter });
      if (search.trim()) params.set("search", search.trim());
      const response = await fetch(`${BASE}/api/customer-invoices/export?${params.toString()}`, {
        credentials: "include",
      });
      if (!response.ok) throw new Error("Invoice report download failed");
      const blob = await response.blob();
      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = "customer-invoice-aging.csv";
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      window.setTimeout(() => URL.revokeObjectURL(objectUrl), 1000);
    } catch {
      toast({
        variant: "destructive",
        title: "Report download failed",
        description: "The invoice report could not be downloaded. Try again.",
      });
    } finally {
      setIsExporting(false);
    }
  }

  function referenceFor(key: string) {
    const existing = attemptReferences.current.get(key);
    if (existing) return existing;
    try {
      const persisted = sessionStorage.getItem(`${ATTEMPT_REFERENCE_PREFIX}${key}`);
      if (persisted) {
        attemptReferences.current.set(key, persisted);
        return persisted;
      }
    } catch {
      // In-memory retry identity still protects repeated clicks in this page session.
    }
    const reference = makeReference();
    attemptReferences.current.set(key, reference);
    try {
      sessionStorage.setItem(`${ATTEMPT_REFERENCE_PREFIX}${key}`, reference);
    } catch {
      // Storage can be disabled; keep the reference in memory for this session.
    }
    return reference;
  }

  function clearReference(key: string) {
    attemptReferences.current.delete(key);
    try {
      sessionStorage.removeItem(`${ATTEMPT_REFERENCE_PREFIX}${key}`);
    } catch {
      // The in-memory reference has already been cleared.
    }
  }

  async function saveDueDate() {
    if (!detail || !dueDateDraft) return;
    try {
      await updateInvoice.mutateAsync({ id: detail.id, data: { dueDate: dueDateDraft } });
      await invalidateInvoice(detail.id);
      toast({ title: "Due date updated", description: `${detail.receiptNumber} now falls due ${dateLabel(dueDateDraft)}.` });
      setDueDateDraft("");
    } catch {
      toast({ title: "Could not update due date", description: "The date was not saved. Check your connection and try again.", variant: "destructive" });
    }
  }

  async function submitPayment() {
    if (!paymentInvoice) return;
    const amount = Number(paymentAmount);
    if (!Number.isFinite(amount) || amount <= 0 || amount > paymentInvoice.balance) return;
    const key = `payment:${paymentInvoice.id}:${amount.toFixed(2)}`;
    const clientReference = referenceFor(key);
    try {
      await recordPayment.mutateAsync({
        id: paymentInvoice.id,
        data: { amount, clientReference },
      });
      clearReference(key);
      await invalidateInvoice(paymentInvoice.id);
      toast({ title: "Payment matched to receipt", description: `${currency(amount)} recorded against ${paymentInvoice.receiptNumber}.` });
      setPaymentInvoice(null);
      setPaymentAmount("");
    } catch {
      toast({ title: "Payment status needs checking", description: "We could not confirm the result. Retry this same amount to safely check the same payment attempt.", variant: "destructive" });
    }
  }

  async function submitReminder() {
    if (!reminderInvoice || (latestIsUnknown && !acknowledgeUnknown)) return;
    const retryAfterUnknown = latestIsUnknown && acknowledgeUnknown
      ? `:after:${reminderInvoice.latestReminder?.id ?? "unknown"}`
      : "";
    const key = `reminder:${reminderInvoice.id}:${reminderChannel}${retryAfterUnknown}`;
    const clientReference = referenceFor(key);
    try {
      const result = await sendReminder.mutateAsync({
        id: reminderInvoice.id,
        data: {
          channel: reminderChannel,
          clientReference,
          ...(latestIsUnknown ? { acknowledgeUnknown: true } : {}),
        },
      });
      clearReference(key);
      await invalidateInvoice(reminderInvoice.id, true);
      const status = result?.status;
      toast({
        title: status === "accepted" ? "Reminder handoff accepted" : status === "failed" ? "Reminder handoff failed" : "Reminder attempt recorded",
        description: status === "accepted"
          ? "The provider accepted the request. This does not confirm delivery."
          : status === "unknown"
            ? "The handoff result is uncertain. Check the reminder history before trying again."
            : status === "failed"
              ? "The provider did not accept this handoff."
              : "Check the invoice reminder history for the latest handoff status.",
        variant: status === "failed" ? "destructive" : undefined,
      });
      setReminderInvoice(null);
      setAcknowledgeUnknown(false);
    } catch {
      toast({ title: "Reminder result is uncertain", description: "The request may have reached the provider. Check the invoice history before retrying.", variant: "destructive" });
      await invalidateInvoice(reminderInvoice.id, true);
    }
  }

  function openDetails(invoice: CustomerInvoice) {
    setSelectedId(invoice.id);
    setDueDateDraft(dateInputValue(invoice.dueDate));
  }

  function openPayment(invoice: CustomerInvoice) {
    setPaymentInvoice(invoice);
    setPaymentAmount(String(invoice.balance));
  }

  function openReminder(invoice: CustomerInvoice, channel: ReminderChannel) {
    setReminderInvoice(invoice);
    setReminderChannel(channel);
    setAcknowledgeUnknown(false);
  }

  const summary = list?.summary;
  const aging = summary ? [
    { name: "No due date", amount: summary.noDueDateAmount, tone: "bg-[#9aa79c]" },
    { name: "Not yet due", amount: summary.notDueAmount, tone: "bg-[#73928a]" },
    { name: "1–30 days", amount: summary.days1To30Amount, tone: "bg-[#d5a04a]" },
    { name: "31–60 days", amount: summary.days31To60Amount, tone: "bg-[#c77e49]" },
    { name: "61–90 days", amount: summary.days61To90Amount, tone: "bg-[#b7604b]" },
    { name: "90+ days", amount: summary.daysOver90Amount, tone: "bg-[#8f453c]" },
  ] : [];
  const agingTotal = aging.reduce((sum, item) => sum + item.amount, 0);

  return (
    <main className="receivables-page min-h-[100dvh] space-y-6 pb-10" data-testid="page-customer-invoices">
      <style>{`.receivables-page h1,.receivables-page h2,.receivables-page h3,.receivables-page [class*="font-display"]{font-family:'Manrope','Outfit',sans-serif}.receivables-page [data-testid^="value-"],.receivables-page [data-testid^="text-total-"],.receivables-page [data-testid^="text-balance-"]{font-variant-numeric:tabular-nums}`}</style>
      <header className="relative overflow-hidden rounded-[1.35rem] bg-[#193c3b] px-5 py-6 text-[#f5f3e9] shadow-sm sm:px-8 sm:py-8">
        <div className="absolute -right-12 -top-20 h-64 w-64 rounded-full border border-white/10" />
        <div className="absolute -right-1 top-2 h-44 w-44 rounded-full border border-white/10" />
        <div className="relative flex flex-col justify-between gap-6 sm:flex-row sm:items-end">
          <div>
            <div className="mb-3 inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#d8e3d9]">
              <ShieldCheck className="h-3.5 w-3.5" /> Receivables workbench
            </div>
            <h1 className="font-display text-3xl font-semibold tracking-[-0.04em] sm:text-[2.65rem]" data-testid="text-page-title">Customer invoices</h1>
            <p className="mt-2 max-w-xl text-sm leading-relaxed text-[#c1d0c8]">Keep credit-sale balances clear, payments matched, and every reminder tied to its receipt.</p>
          </div>
          <div className="grid grid-cols-2 gap-3 sm:min-w-[260px]">
            <div className="rounded-xl border border-white/10 bg-white/[0.06] px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.13em] text-[#b7c9c0]">Open receivables</p>
              <p className="mt-1 font-mono text-xl font-semibold tabular-nums" data-testid="value-open-amount">{summary ? currency(summary.openAmount) : "—"}</p>
            </div>
            <div className="rounded-xl border border-[#d9a64f]/30 bg-[#d9a64f]/10 px-4 py-3">
              <p className="text-[11px] uppercase tracking-[0.13em] text-[#e5c98f]">Past due</p>
              <p className="mt-1 font-mono text-xl font-semibold tabular-nums text-[#f0d18d]" data-testid="value-overdue-amount">{summary ? currency(summary.overdueAmount) : "—"}</p>
            </div>
          </div>
        </div>
      </header>

      <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Receivables summary">
        <Card className="border-[#dce4dd] bg-[#fbfcf8] shadow-none">
          <CardContent className="flex items-center justify-between gap-4 p-4 sm:p-5">
            <div>
              <p className="text-xs font-medium text-[#667a73]">Open receipts</p>
              <p className="mt-1 font-display text-2xl font-semibold tracking-tight text-[#193c3b]" data-testid="value-open-count">{summary?.openCount ?? "—"}</p>
            </div>
            <div className="rounded-xl bg-[#e8f0eb] p-3 text-[#356252]"><ReceiptText className="h-5 w-5" /></div>
          </CardContent>
        </Card>
        <Card className="border-[#e9dfcb] bg-[#fffdf8] shadow-none">
          <CardContent className="flex items-center justify-between gap-4 p-4 sm:p-5">
            <div>
              <p className="text-xs font-medium text-[#877150]">Overdue receipts</p>
              <p className="mt-1 font-display text-2xl font-semibold tracking-tight text-[#895522]" data-testid="value-overdue-count">{summary?.overdueCount ?? "—"}</p>
            </div>
            <div className="rounded-xl bg-[#f8efd9] p-3 text-[#a76c24]"><Clock3 className="h-5 w-5" /></div>
          </CardContent>
        </Card>
        <Card className="border-[#dce4dd] bg-[#fbfcf8] shadow-none">
          <CardContent className="flex items-center justify-between gap-4 p-4 sm:p-5">
            <div>
              <p className="text-xs font-medium text-[#667a73]">Not yet due</p>
              <p className="mt-1 font-display text-2xl font-semibold tracking-tight text-[#193c3b]" data-testid="value-not-due-amount">{summary ? currency(summary.notDueAmount) : "—"}</p>
            </div>
            <div className="rounded-xl bg-[#e8f0eb] p-3 text-[#356252]"><CalendarDays className="h-5 w-5" /></div>
          </CardContent>
        </Card>
        <Card className="border-[#dce4dd] bg-[#fbfcf8] shadow-none">
          <CardContent className="flex items-center justify-between gap-4 p-4 sm:p-5">
            <div>
              <p className="text-xs font-medium text-[#667a73]">Missing due date</p>
              <p className="mt-1 font-display text-2xl font-semibold tracking-tight text-[#193c3b]" data-testid="value-no-due-date-amount">{summary ? currency(summary.noDueDateAmount) : "—"}</p>
              <p className="mt-1 text-[10px] text-[#829087]">{summary?.noDueDateCount ?? "—"} receipts</p>
            </div>
            <div className="rounded-xl bg-[#edf0e9] p-3 text-[#67776e]"><CalendarDays className="h-5 w-5" /></div>
          </CardContent>
        </Card>
      </section>

      {summary && agingTotal > 0 && (
        <section className="rounded-2xl border border-[#e1e5da] bg-[#fbfcf8] p-4 sm:p-5" data-testid="section-aging-summary">
          <div className="mb-3 flex flex-wrap items-end justify-between gap-2">
            <div>
              <h2 className="font-display text-base font-semibold text-[#193c3b]">Age of outstanding balance</h2>
              <p className="mt-0.5 text-xs text-[#74817a]">A quick read on how long open balances have been waiting.</p>
            </div>
            <span className="font-mono text-xs tabular-nums text-[#60746d]" data-testid="value-aging-total">{currency(agingTotal)}</span>
          </div>
          <div className="flex h-2.5 overflow-hidden rounded-full bg-[#e8ebe4]" aria-label="Receivables by age">
            {aging.map((item) => item.amount > 0 && <span key={item.name} className={`${item.tone} transition-all`} style={{ width: `${(item.amount / agingTotal) * 100}%` }} title={`${item.name}: ${currency(item.amount)}`} />)}
          </div>
          <div className="mt-3 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 xl:grid-cols-6">
            {aging.map((item) => (
              <div key={item.name} className="flex min-w-0 items-center gap-2">
                <span className={`h-2 w-2 shrink-0 rounded-full ${item.tone}`} />
                <span className="truncate text-[11px] text-[#6f7d75]">{item.name}</span>
                <span className="ml-auto whitespace-nowrap font-mono text-[11px] tabular-nums text-[#435a52]" data-testid={`value-aging-${item.name.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>{currency(item.amount)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="overflow-hidden rounded-2xl border border-[#dce4dd] bg-[#fbfcf8] shadow-sm">
        <div className="border-b border-[#e8ebe4] px-4 py-4 sm:px-5">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="font-display text-lg font-semibold tracking-tight text-[#193c3b]">Credit-sale receipts</h2>
              <p className="mt-0.5 text-xs text-[#718078]" data-testid="text-invoice-count">
                {invoiceQuery.isLoading ? "Loading receipts…" : `${visibleInvoices.length} ${visibleInvoices.length === 1 ? "receipt" : "receipts"} on this page`}
              </p>
            </div>
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
              <div className="relative min-w-0 sm:w-64">
                <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#829087]" />
                <Input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Find customer, contact, or receipt"
                  className="h-10 border-[#dfe5dc] bg-[#f6f8f3] pl-9 text-sm focus-visible:ring-[#477368]"
                  data-testid="input-search-invoices"
                  aria-label="Search customers, contact details, or receipt numbers"
                />
                {search && <button type="button" onClick={() => setSearch("")} aria-label="Clear search" className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-1 text-[#87938c] hover:bg-[#e9ede7]" data-testid="button-clear-search"><X className="h-3.5 w-3.5" /></button>}
              </div>
              <Button
                type="button"
                variant="outline"
                onClick={() => void downloadInvoiceReport()}
                disabled={isExporting}
                className="h-10 shrink-0 border-[#d2ddd4] bg-[#fbfcf8] text-xs text-[#31594f] hover:bg-[#edf3ee]"
                data-testid="button-download-invoice-report"
              >
                <Download className="mr-2 h-4 w-4" />
                {isExporting ? "Preparing…" : "Download CSV"}
              </Button>
              <div className="flex overflow-x-auto rounded-lg border border-[#e2e7df] bg-[#f3f5ef] p-1 no-scrollbar" role="tablist" aria-label="Invoice status filter">
                {filters.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    role="tab"
                    aria-selected={filter === option.value}
                    onClick={() => {
                      setCursorStack([null]);
                      setFilter(option.value);
                    }}
                    className={`whitespace-nowrap rounded-md px-3 py-1.5 text-xs font-semibold transition-colors ${filter === option.value ? "bg-[#193c3b] text-[#f5f3e9] shadow-sm" : "text-[#66766e] hover:bg-white"}`}
                    data-testid={`tab-filter-${option.value}`}
                  >{option.label}</button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {invoiceQuery.isLoading ? (
          <div className="space-y-3 p-4" data-testid="state-invoices-loading" aria-label="Loading customer invoices">
            {[0, 1, 2, 3].map((item) => <div key={item} className="h-[76px] animate-pulse rounded-xl bg-[#edf0e9]" />)}
          </div>
        ) : invoiceQuery.isError ? (
          <div className="flex flex-col items-center px-5 py-12 text-center" data-testid="state-invoices-error">
            <div className="mb-3 rounded-full bg-[#f8e8e2] p-3 text-[#a34d38]"><AlertCircle className="h-5 w-5" /></div>
            <p className="font-semibold text-[#284541]">Receipts did not load</p>
            <p className="mt-1 max-w-sm text-sm text-[#74817a]">Your invoice data is unchanged. Check your connection, then try again.</p>
            <Button variant="outline" className="mt-4 border-[#ccd8cf] text-[#31594f]" onClick={() => invoiceQuery.refetch()} data-testid="button-retry-invoices">Try again</Button>
          </div>
        ) : visibleInvoices.length === 0 ? (
          <div className="flex flex-col items-center px-5 py-14 text-center" data-testid="state-invoices-empty">
            <div className="mb-3 rounded-full bg-[#e8f0eb] p-4 text-[#477368]"><CheckCircle2 className="h-6 w-6" /></div>
            <p className="font-display text-lg font-semibold text-[#193c3b]">{search ? "No matching receipts" : filter === "settled" ? "No settled receipts here" : "No outstanding receipts"}</p>
            <p className="mt-1 max-w-sm text-sm text-[#74817a]">{search ? "Try another name, phone number, or receipt number." : "When a completed credit sale has a balance, it will appear here."}</p>
            {search && <Button variant="ghost" className="mt-2 text-[#356252]" onClick={() => setSearch("")} data-testid="button-reset-search">Clear search</Button>}
          </div>
        ) : (
          <>
            <div className="hidden grid-cols-[minmax(190px,1.4fr)_minmax(125px,.85fr)_minmax(115px,.8fr)_minmax(110px,.8fr)_minmax(110px,.8fr)_auto] gap-4 bg-[#f2f5ef] px-5 py-2.5 text-[10px] font-semibold uppercase tracking-[0.12em] text-[#78867d] lg:grid">
              <span>Customer · receipt</span><span>Sale date</span><span>Due date</span><span className="text-right">Sale total</span><span className="text-right">Balance</span><span className="text-right">Status</span>
            </div>
            <div className="divide-y divide-[#e9ece5]">
              {visibleInvoices.map((invoice) => (
                <article key={invoice.id} className="group px-4 py-4 transition-colors hover:bg-[#f5f7f2] sm:px-5" data-testid={`row-invoice-${invoice.id}`}>
                  <div className="flex flex-col gap-3 lg:grid lg:grid-cols-[minmax(190px,1.4fr)_minmax(125px,.85fr)_minmax(115px,.8fr)_minmax(110px,.8fr)_minmax(110px,.8fr)_auto] lg:items-center lg:gap-4">
                    <button type="button" onClick={() => openDetails(invoice)} className="flex min-w-0 items-center gap-3 text-left" data-testid={`button-open-invoice-${invoice.id}`}>
                      <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#e7efea] text-[#3b6859]"><ReceiptText className="h-[18px] w-[18px]" /></span>
                      <span className="min-w-0">
                        <span className="block truncate text-sm font-semibold text-[#263f3a]">{invoice.customerName}</span>
                        <span className="mt-0.5 block truncate font-mono text-[11px] text-[#758179]" data-testid={`text-receipt-number-${invoice.id}`}>{invoice.receiptNumber}</span>
                      </span>
                    </button>
                    <div className="flex items-center justify-between text-xs lg:block"><span className="text-[#87928a] lg:hidden">Sale date</span><span className="text-[#566961]" data-testid={`text-sale-date-${invoice.id}`}>{dateLabel(invoice.createdAt)}</span></div>
                    <div className="flex items-center justify-between text-xs lg:block"><span className="text-[#87928a] lg:hidden">Due</span><span className="text-[#566961]" data-testid={`text-due-date-${invoice.id}`}>{dateLabel(invoice.dueDate)}</span></div>
                    <div className="flex items-center justify-between text-xs lg:block lg:text-right"><span className="text-[#87928a] lg:hidden">Sale total</span><span className="font-mono tabular-nums text-[#52655d]" data-testid={`text-total-${invoice.id}`}>{currency(invoice.totalAmount)}</span></div>
                    <div className="flex items-center justify-between text-xs lg:block lg:text-right"><span className="text-[#87928a] lg:hidden">Balance due</span><span className="font-mono text-sm font-semibold tabular-nums text-[#193c3b]" data-testid={`text-balance-${invoice.id}`}>{currency(invoice.balance)}</span></div>
                    <div className="flex items-center justify-between gap-2 lg:justify-end">
                      <div className="flex items-center gap-2">
                        <InvoiceStatus invoice={invoice} />
                        <span className={`rounded-full px-2 py-1 text-[10px] font-semibold ${agingTone(invoice)}`} data-testid={`status-aging-${invoice.id}`}>{agingText(invoice)}</span>
                      </div>
                      <button type="button" className="rounded-md p-1.5 text-[#75857c] transition-colors hover:bg-[#e6ede6] hover:text-[#31594f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#477368]" onClick={() => openDetails(invoice)} aria-label={`View ${invoice.receiptNumber}`} data-testid={`button-details-${invoice.id}`}><ChevronRight className="h-4 w-4" /></button>
                    </div>
                  </div>
                  <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-[#edf0ea] pt-3 lg:ml-[52px]">
                    <span className="text-[11px] text-[#7a8780]">
                      {invoice.reminderCount > 0 ? `Reminder attempts: ${invoice.reminderCount}` : "No reminder sent"}
                      {invoice.latestReminder && <> · <span data-testid={`text-latest-reminder-${invoice.id}`}>{invoice.latestReminder.status === "unknown" ? "Last handoff uncertain" : `Last handoff ${invoice.latestReminder.status}`}</span></>}
                    </span>
                    <div className="flex items-center gap-2">
                      {invoice.status !== "settled" && (
                        <>
                          <Button variant="outline" size="sm" className="h-8 border-[#d2ddd4] bg-[#fbfcf8] px-2.5 text-xs text-[#31594f] hover:bg-[#edf3ee]" onClick={() => openReminder(invoice, "sms")} disabled={!invoice.customerPhone} data-testid={`button-remind-sms-${invoice.id}`}><MessageSquareText className="mr-1.5 h-3.5 w-3.5" /> SMS reminder</Button>
                          {invoice.customerEmail && <Button variant="outline" size="sm" className="h-8 border-[#d2ddd4] bg-[#fbfcf8] px-2.5 text-xs text-[#31594f] hover:bg-[#edf3ee]" onClick={() => openReminder(invoice, "email")} data-testid={`button-remind-email-${invoice.id}`}><Mail className="mr-1.5 h-3.5 w-3.5" /> Email</Button>}
                          <Button size="sm" className="h-8 bg-[#286052] px-3 text-xs text-white hover:bg-[#204e43]" onClick={() => openPayment(invoice)} data-testid={`button-record-payment-${invoice.id}`}><CreditCard className="mr-1.5 h-3.5 w-3.5" /> Record payment</Button>
                        </>
                      )}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}
        {!invoiceQuery.isLoading && !invoiceQuery.isError && list &&
          (invoices.length > 0 || cursorStack.length > 1) && (
            <div className="flex flex-wrap items-center justify-between gap-3 border-t border-[#e8ebe4] px-4 py-3 sm:px-5" data-testid="pagination-invoices" aria-label="Invoice result pages">
              <span className="text-xs text-[#718078]" data-testid="text-invoice-page">
                Page {cursorStack.length} · {invoices.length} {invoices.length === 1 ? "receipt" : "receipts"}
              </span>
              <div className="flex items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={cursorStack.length <= 1}
                  onClick={() => setCursorStack((pages) => pages.length > 1 ? pages.slice(0, -1) : pages)}
                  className="h-8 border-[#d2ddd4] text-xs text-[#31594f]"
                  data-testid="button-invoices-previous"
                >Previous</Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  disabled={!list.nextCursor}
                  onClick={() => {
                    if (list.nextCursor) setCursorStack((pages) => [...pages, list.nextCursor!]);
                  }}
                  className="h-8 border-[#d2ddd4] text-xs text-[#31594f]"
                  data-testid="button-invoices-next"
                >Next</Button>
              </div>
            </div>
          )}
      </section>

      <div className="flex items-start gap-2 rounded-xl border border-[#e6e5d9] bg-[#f7f6ee] px-4 py-3 text-xs leading-relaxed text-[#68776e]" data-testid="notice-reminder-handoff">
        <ShieldCheck className="mt-0.5 h-4 w-4 shrink-0 text-[#6a8678]" />
        <p>Reminder status shows whether the message handoff was accepted or uncertain. It is not proof that a customer received or read it. Reminders are sent only when a staff member starts them.</p>
      </div>

      <Dialog open={selectedId !== null} onOpenChange={(open) => { if (!open) { setSelectedId(null); setDueDateDraft(""); } }}>
        <DialogContent className="max-h-[90dvh] max-w-2xl overflow-y-auto border-[#dbe3db] bg-[#fbfcf8] p-0">
          {detailQuery.isLoading ? (
            <div className="space-y-4 p-6" data-testid="state-invoice-detail-loading"><div className="h-7 w-48 animate-pulse rounded bg-[#e8ede6]" /><div className="h-24 animate-pulse rounded-xl bg-[#edf0e9]" /><div className="h-40 animate-pulse rounded-xl bg-[#edf0e9]" /></div>
          ) : detailQuery.isError || !detail ? (
            <div className="p-7 text-center" data-testid="state-invoice-detail-error"><AlertCircle className="mx-auto mb-3 h-7 w-7 text-[#a34d38]" /><p className="font-semibold text-[#284541]">Receipt details unavailable</p><p className="mt-1 text-sm text-[#74817a]">Try opening the receipt again in a moment.</p><Button className="mt-4 bg-[#286052]" onClick={() => selectedId && detailQuery.refetch()} data-testid="button-retry-invoice-detail">Try again</Button></div>
          ) : (
            <>
              <div className="bg-[#193c3b] px-5 py-5 text-[#f5f3e9] sm:px-7">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <div className="mb-2 flex items-center gap-2 text-xs text-[#c1d0c8]"><ReceiptText className="h-4 w-4" /> Completed credit sale</div>
                    <DialogTitle className="font-display text-2xl font-semibold tracking-tight text-[#f5f3e9]" data-testid="text-detail-customer">{detail.customerName}</DialogTitle>
                    <DialogDescription className="mt-1 font-mono text-xs text-[#c1d0c8]" data-testid="text-detail-receipt">{detail.receiptNumber}</DialogDescription>
                  </div>
                  <InvoiceStatus invoice={detail} />
                </div>
                <div className="mt-5 grid grid-cols-3 gap-2 border-t border-white/10 pt-4">
                  <div><p className="text-[10px] uppercase tracking-wider text-[#b7c9c0]">Sale total</p><p className="mt-1 font-mono text-sm tabular-nums">{currency(detail.totalAmount)}</p></div>
                  <div><p className="text-[10px] uppercase tracking-wider text-[#b7c9c0]">Paid</p><p className="mt-1 font-mono text-sm tabular-nums">{currency(detail.amountPaid)}</p></div>
                  <div><p className="text-[10px] uppercase tracking-wider text-[#e3c887]">Still due</p><p className="mt-1 font-mono text-sm font-semibold tabular-nums text-[#f0d18d]" data-testid="value-detail-balance">{currency(detail.balance)}</p></div>
                </div>
              </div>
              <div className="space-y-5 p-5 sm:p-7">
                <section className="grid gap-3 sm:grid-cols-2">
                  <div className="rounded-xl border border-[#e2e8df] bg-[#f5f7f2] p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.11em] text-[#819087]">Customer</p>
                    <p className="mt-2 text-sm font-semibold text-[#29433d]">{detail.customerName}</p>
                    {detail.customerPhone && <p className="mt-1 text-xs text-[#687970]" data-testid="text-detail-phone">{detail.customerPhone}</p>}
                    {detail.customerEmail && <p className="mt-1 break-all text-xs text-[#687970]" data-testid="text-detail-email">{detail.customerEmail}</p>}
                  </div>
                  <div className="rounded-xl border border-[#e2e8df] bg-[#f5f7f2] p-4">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.11em] text-[#819087]">Due date</p>
                    <p className="mt-2 text-sm font-semibold text-[#29433d]" data-testid="text-detail-due-date">{dateLabel(detail.dueDate)}</p>
                    <div className="mt-3 flex gap-2">
                      <Input type="date" value={dueDateDraft} onChange={(event) => setDueDateDraft(event.target.value)} className="h-9 min-w-0 border-[#d6dfd7] bg-white text-xs" aria-label="New due date" data-testid="input-due-date" />
                      <Button size="sm" className="h-9 shrink-0 bg-[#286052] px-3 text-xs" disabled={!dueDateDraft || dueDateDraft === dateInputValue(detail.dueDate) || updateInvoice.isPending} onClick={saveDueDate} data-testid="button-save-due-date">{updateInvoice.isPending ? "Saving…" : "Save"}</Button>
                    </div>
                    <p className="mt-2 text-[10px] text-[#819087]">{detail.dueDate ? "Use the date the customer and store agreed to." : "This receipt has no due date yet. Set the agreed date to include it in aging."}</p>
                  </div>
                </section>

                <section>
                  <div className="mb-2 flex items-center justify-between">
                    <h3 className="font-display text-base font-semibold text-[#29433d]">Sale items</h3>
                    <span className="text-xs text-[#869189]" data-testid="text-item-count">{detail.items.length} items</span>
                  </div>
                  <div className="overflow-hidden rounded-xl border border-[#e2e8df]">
                    {detail.items.map((item, index) => (
                      <div key={`${item.productName}-${index}`} className="flex items-center justify-between gap-4 border-b border-[#edf0ea] px-3.5 py-3 last:border-b-0" data-testid={`row-sale-item-${index}`}>
                        <div className="min-w-0"><p className="truncate text-sm font-medium text-[#365149]">{item.productName}</p><p className="mt-0.5 text-[11px] text-[#809087]">{item.quantity} × {currency(item.unitPrice)}</p></div>
                        <span className="whitespace-nowrap font-mono text-sm tabular-nums text-[#29433d]">{currency(item.total)}</span>
                      </div>
                    ))}
                    <div className="space-y-1 border-t border-[#dfe6de] bg-[#f5f7f2] px-3.5 py-3 text-xs">
                      <div className="flex justify-between text-[#718078]"><span>Discount</span><span className="font-mono tabular-nums">{currency(detail.discount)}</span></div>
                      <div className="flex justify-between text-[#718078]"><span>Tax</span><span className="font-mono tabular-nums">{currency(detail.tax)}</span></div>
                    </div>
                  </div>
                  {detail.notes && <p className="mt-2 rounded-lg bg-[#f5f2e7] px-3 py-2 text-xs text-[#756849]" data-testid="text-detail-notes">Sale note: {detail.notes}</p>}
                </section>

                <section>
                  <h3 className="mb-2 font-display text-base font-semibold text-[#29433d]">Matched payments</h3>
                  {detail.payments.length ? (
                    <div className="space-y-2">
                      {detail.payments.map((payment) => (
                        <div key={payment.id} className="flex items-center justify-between gap-3 rounded-xl border border-[#e2e8df] bg-white px-3.5 py-3" data-testid={`row-payment-${payment.id}`}>
                          <div className="flex items-center gap-3"><span className="rounded-lg bg-[#e7f0e9] p-2 text-[#3f755e]"><Check className="h-4 w-4" /></span><div><p className="text-xs font-semibold capitalize text-[#38534a]">{payment.kind} payment</p><p className="mt-0.5 text-[11px] text-[#809087]">{dateLabel(payment.createdAt)}</p></div></div>
                          <span className="font-mono text-sm font-semibold tabular-nums text-[#2e6450]">{currency(payment.amount)}</span>
                        </div>
                      ))}
                    </div>
                  ) : <p className="rounded-xl border border-dashed border-[#d7e0d7] px-4 py-4 text-center text-xs text-[#829087]" data-testid="empty-matched-payments">No payments have been matched to this sale.</p>}
                </section>

                <section>
                  <div className="mb-2 flex items-center justify-between gap-2"><h3 className="font-display text-base font-semibold text-[#29433d]">Reminder handoffs</h3><span className="text-[10px] text-[#829087]">Staff initiated</span></div>
                  {remindersQuery.isError ? (
                    <div className="rounded-xl border border-[#e8d8cc] bg-[#fcf4ef] px-3 py-3 text-xs text-[#92533f]" data-testid="state-reminders-error">Reminder history could not be refreshed. The current status may be incomplete.</div>
                  ) : reminderHistory.length ? (
                    <div className="space-y-2">
                      {reminderHistory.map((reminder) => (
                        <div key={reminder.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-[#e2e8df] bg-white px-3.5 py-3" data-testid={`row-reminder-${reminder.id}`}>
                          <div className="flex items-center gap-3"><span className="rounded-lg bg-[#edf1eb] p-2 text-[#5a7869]">{reminder.channel === "sms" ? <MessageSquareText className="h-4 w-4" /> : <Mail className="h-4 w-4" />}</span><div><p className="text-xs font-semibold uppercase text-[#38534a]">{reminder.channel} · {reminder.recipient}</p><p className="mt-0.5 text-[11px] text-[#809087]">{dateLabel(reminder.createdAt)}{reminder.completedAt ? ` · completed ${dateLabel(reminder.completedAt)}` : ""}</p></div></div>
                          <ReminderStatus status={reminder.status} />
                        </div>
                      ))}
                    </div>
                  ) : <p className="rounded-xl border border-dashed border-[#d7e0d7] px-4 py-4 text-center text-xs text-[#829087]" data-testid="empty-reminder-history">No reminder attempts for this receipt.</p>}
                  <p className="mt-2 text-[10px] leading-relaxed text-[#829087]">Accepted means the provider accepted a handoff request. It does not confirm delivery.</p>
                </section>
                <div className="flex flex-col-reverse gap-2 border-t border-[#e8ebe4] pt-4 sm:flex-row sm:justify-between">
                  <Button variant="outline" className="border-[#d4ded5] text-[#45645a]" onClick={() => openReminder(detail, "sms")} disabled={detail.status === "settled" || !detail.customerPhone} data-testid="button-detail-remind-sms"><MessageSquareText className="mr-2 h-4 w-4" /> Send SMS reminder</Button>
                  <div className="flex flex-col-reverse gap-2 sm:flex-row">
                    {detail.customerEmail && <Button variant="outline" className="border-[#d4ded5] text-[#45645a]" onClick={() => openReminder(detail, "email")} disabled={detail.status === "settled"} data-testid="button-detail-remind-email"><Mail className="mr-2 h-4 w-4" /> Send email</Button>}
                    <Button className="bg-[#286052] hover:bg-[#204e43]" onClick={() => openPayment(detail)} disabled={detail.status === "settled"} data-testid="button-detail-record-payment"><CreditCard className="mr-2 h-4 w-4" /> Record payment</Button>
                  </div>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!paymentInvoice} onOpenChange={(open) => { if (!open && !recordPayment.isPending) { setPaymentInvoice(null); setPaymentAmount(""); } }}>
        <DialogContent className="max-w-md border-[#dbe3db] bg-[#fbfcf8]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#193c3b]">Record a matched payment</DialogTitle>
            <DialogDescription className="text-sm text-[#718078]">This payment will be recorded against the original credit sale receipt.</DialogDescription>
          </DialogHeader>
          {paymentInvoice && <div className="space-y-4">
            <div className="rounded-xl border border-[#e1e7df] bg-[#f3f6f0] p-4">
              <div className="flex justify-between gap-3 text-xs"><span className="text-[#7b8980]">Customer</span><span className="text-right font-semibold text-[#365149]">{paymentInvoice.customerName}</span></div>
              <div className="mt-2 flex justify-between gap-3 text-xs"><span className="text-[#7b8980]">Receipt</span><span className="font-mono text-[#365149]" data-testid="text-payment-receipt">{paymentInvoice.receiptNumber}</span></div>
              <div className="mt-2 flex justify-between gap-3 border-t border-[#e0e6dd] pt-2 text-xs"><span className="text-[#7b8980]">Remaining balance</span><span className="font-mono font-semibold tabular-nums text-[#193c3b]" data-testid="text-payment-balance">{currency(paymentInvoice.balance)}</span></div>
            </div>
            <div>
              <label htmlFor="payment-amount" className="text-xs font-semibold text-[#52665d]">Amount received (₦)</label>
              <Input id="payment-amount" type="number" inputMode="decimal" min="0.01" max={paymentInvoice.balance} step="0.01" value={paymentAmount} onChange={(event) => setPaymentAmount(event.target.value)} className="mt-1.5 h-11 border-[#d5dfd7] bg-white font-mono text-lg tabular-nums" autoFocus data-testid="input-payment-amount" />
              <p className="mt-1.5 text-[11px] text-[#839087]">Enter an amount up to {currency(paymentInvoice.balance)}. The remaining balance stays open.</p>
            </div>
            <div className="flex items-start gap-2 rounded-lg bg-[#eaf1ec] px-3 py-2.5 text-[11px] leading-relaxed text-[#5c7468]" data-testid="notice-payment-match"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" /><p>Payments stay matched to this receipt and its original sale; no other customer balance is changed.</p></div>
          </div>}
          <DialogFooter>
            <Button variant="outline" disabled={recordPayment.isPending} className="border-[#d5dfd7]" onClick={() => { setPaymentInvoice(null); setPaymentAmount(""); }} data-testid="button-cancel-payment">Cancel</Button>
            <Button className="bg-[#286052] hover:bg-[#204e43]" disabled={recordPayment.isPending || !paymentAmount || Number(paymentAmount) <= 0 || Number(paymentAmount) > (paymentInvoice?.balance ?? 0)} onClick={submitPayment} data-testid="button-confirm-payment">
              {recordPayment.isPending ? "Recording…" : "Confirm payment"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reminderInvoice} onOpenChange={(open) => { if (!open && !sendReminder.isPending) { setReminderInvoice(null); setAcknowledgeUnknown(false); } }}>
        <DialogContent className="max-w-md border-[#dbe3db] bg-[#fbfcf8]">
          <DialogHeader>
            <DialogTitle className="font-display text-xl text-[#193c3b]">Send invoice reminder</DialogTitle>
            <DialogDescription className="text-sm text-[#718078]">A staff-started handoff for this completed credit sale.</DialogDescription>
          </DialogHeader>
          {reminderInvoice && <div className="space-y-4">
            <div className="rounded-xl border border-[#e1e7df] bg-[#f3f6f0] p-4">
              <p className="text-xs font-semibold text-[#365149]">{reminderInvoice.customerName}</p>
              <p className="mt-1 font-mono text-[11px] text-[#708078]" data-testid="text-reminder-receipt">{reminderInvoice.receiptNumber}</p>
              <div className="mt-3 flex justify-between border-t border-[#e0e6dd] pt-2 text-xs"><span className="text-[#7b8980]">Balance due</span><span className="font-mono font-semibold tabular-nums text-[#193c3b]">{currency(reminderInvoice.balance)}</span></div>
            </div>
            <div className="grid grid-cols-2 gap-2" role="group" aria-label="Reminder channel">
              <button type="button" onClick={() => setReminderChannel("sms")} disabled={!reminderInvoice.customerPhone} className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${reminderChannel === "sms" ? "border-[#477368] bg-[#eaf1ec] text-[#31594f]" : "border-[#e0e6de] bg-white text-[#718078]"}`} data-testid="button-channel-sms"><MessageSquareText className="h-4 w-4" /> SMS</button>
              <button type="button" onClick={() => setReminderChannel("email")} disabled={!reminderInvoice.customerEmail} className={`flex items-center justify-center gap-2 rounded-xl border px-3 py-3 text-sm font-semibold transition-colors disabled:cursor-not-allowed disabled:opacity-40 ${reminderChannel === "email" ? "border-[#477368] bg-[#eaf1ec] text-[#31594f]" : "border-[#e0e6de] bg-white text-[#718078]"}`} data-testid="button-channel-email"><Mail className="h-4 w-4" /> Email</button>
            </div>
            <div className="rounded-lg bg-[#f5f6ef] px-3 py-2.5 text-xs text-[#67776e]">
              <p className="font-semibold text-[#496158]">Recipient on file</p>
              <p className="mt-1" data-testid="text-reminder-recipient">{reminderChannel === "sms" ? reminderInvoice.customerPhone || "No phone number on file" : reminderInvoice.customerEmail || "No email address on file"}</p>
            </div>
            {latestIsUnknown && (
              <div className="rounded-xl border border-[#e8d7b6] bg-[#fff6e3] p-3.5" data-testid="notice-unknown-reminder">
                <div className="flex gap-2 text-[#815315]"><AlertCircle className="mt-0.5 h-4 w-4 shrink-0" /><p className="text-xs font-semibold leading-relaxed">The last handoff result is uncertain. Starting another reminder could send a duplicate.</p></div>
                <label className="mt-3 flex cursor-pointer items-start gap-2 text-xs leading-relaxed text-[#6f5a36]">
                  <input type="checkbox" checked={acknowledgeUnknown} onChange={(event) => setAcknowledgeUnknown(event.target.checked)} className="mt-0.5 h-4 w-4 accent-[#286052]" data-testid="checkbox-acknowledge-unknown" />
                  I checked the previous attempt and want to start another handoff.
                </label>
              </div>
            )}
            <p className="flex items-start gap-2 text-[11px] leading-relaxed text-[#7e8b82]" data-testid="notice-reminder-not-delivery"><ShieldCheck className="mt-0.5 h-4 w-4 shrink-0" />A successful handoff is not confirmation of delivery to a phone or inbox.</p>
          </div>}
          <DialogFooter>
            <Button variant="outline" disabled={sendReminder.isPending} className="border-[#d5dfd7]" onClick={() => { setReminderInvoice(null); setAcknowledgeUnknown(false); }} data-testid="button-cancel-reminder">Cancel</Button>
            <Button className="bg-[#286052] hover:bg-[#204e43]" disabled={sendReminder.isPending || (latestIsUnknown && !acknowledgeUnknown) || (reminderChannel === "sms" ? !reminderInvoice?.customerPhone : !reminderInvoice?.customerEmail)} onClick={submitReminder} data-testid="button-confirm-reminder">
              {sendReminder.isPending ? "Starting handoff…" : <><Bell className="mr-2 h-4 w-4" /> Start {reminderChannel.toUpperCase()} handoff</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </main>
  );
}
